import { ActionType, createReducer } from 'typesafe-actions';
import { omit, isEmpty } from 'lodash';
import { v4 as uuidv4 } from 'uuid';
import { RothConversionActionTypes as act } from './actionTypes';
import * as RothConversionActions from './actions';
import { RothConversionState } from './types';
import { Fund } from 'src/components/Exchange/types';
import {
  UNIT,
  BENEFICIARY_TYPES,
  ROTH_CONVERSION_ALLOCATION_TYPES,
  ROTH_CONVERSION_TAX_OPTIONS,
} from 'src/settings/settings';
export * from './actions';

const initialState: RothConversionState = {
  convertFromFunds: {},
  allocateToFunds: {},
  beneficiaries: [],
  beneficiaryOption: null,
  error: null,
  errorMessage: null,
  allocationType: ROTH_CONVERSION_ALLOCATION_TYPES.SAME_FUNDS,
  taxOption: ROTH_CONVERSION_TAX_OPTIONS.YES_WITHHOLD,
  taxPercentage: 10,
  termsConditions: false,
  userAddedFunds: [],
  userAddedBeneficiaries: {
    primary: {
      0: {
        entityType: BENEFICIARY_TYPES.INDIVIDUAL,
      },
    },
    secondary: {},
  },
};

export default createReducer<
  RothConversionState,
  ActionType<typeof RothConversionActions>
>(initialState)
  // SELECT_CONVERSION_FUND
  .handleAction(act.SELECT_CONVERSION_FUND, (state, { payload }) => {
    const { agreementID, props, step } = payload;
    const fundListChanged = step === 1 ? 'convertFromFunds' : 'allocateToFunds';
    return {
      ...state,
      [fundListChanged]: {
        ...state[fundListChanged],
        [agreementID]: {
          ...state[fundListChanged][agreementID],
          selected: true,
          ...props,
        },
      },
    };
  })
  // DESELECT FUND
  .handleAction(act.DESELECT_CONVERSION_FUND, (state, { payload }) => {
    const { agreementID, step } = payload;
    const fundListChanged = step === 1 ? 'convertFromFunds' : 'allocateToFunds';

    return {
      ...state,
      [fundListChanged]: omit(state[fundListChanged], [agreementID]),
    };
  })

  // BATCH SELECTION OF FUNDS
  .handleAction(act.BATCH_SELECT_CONVERSION_FUND, (state, { payload }) => {
    const { fundsMap, step } = payload;
    const fundListChanged = step === 1 ? 'convertFromFunds' : 'allocateToFunds';
    return {
      ...state,
      [fundListChanged]: {
        ...state[fundListChanged],
        ...fundsMap,
      },
    };
  })

  // RESET TRANSACTION
  .handleAction(act.RESET_TRANSACTION, (state) => {
    return {
      ...state,
      convertFromFunds: {},
      convertToFunds: {},
    };
  })

  .handleAction(act.UPDATE_CONVERSION_FUND, (state, { payload }) => {
    const { agreementID, props, step } = payload;
    const fundListChanged = step === 1 ? 'convertFromFunds' : 'allocateToFunds';
    const fundChanged = state[fundListChanged][agreementID];

    return {
      ...state,
      [fundListChanged]: {
        ...state[fundListChanged],
        [agreementID]: {
          ...fundChanged,
          ...props,
        },
      },
    };
  })

  .handleAction(act.UPDATE_FUND_ALLOCATION_TYPE, (state, { payload }) => {
    const { allocationType } = payload;
    return {
      ...state,
      allocationType,
    };
  })

  // Editing state after user chooses roth Tax Option
  .handleAction(act.UPDATE_TAX_OPTION, (state, { payload }) => {
    const { taxOption } = payload;
    return {
      ...state,
      taxOption,
    };
  })

  // Editing state after user chooses roth Tax percentage
  .handleAction(act.UPDATE_TAX_PERCENTAGE, (state, { payload }) => {
    const { taxPercentage } = payload;
    return {
      ...state,
      taxPercentage,
    };
  })

  // Editing state after user chooses rothconversion user agrees to terms
  .handleAction(act.UPDATE_TERMS_AND_CONDITIONS, (state, { payload }) => {
    const { termsConditions } = payload;
    return {
      ...state,
      termsConditions,
    };
  })

  // Editing state after user choses roth allocation type
  .handleAction(act.ADJUST_FOR_ROTH_ALLOCATION, (state) => {
    return {
      ...state,
      allocateToFunds: {},
    };
  })

  .handleAction(act.UPDATE_FUND_ALLOCATE_TOTAL, (state, { payload }) => {
    const { allocateToFunds } = state;
    const { agreementID, props } = payload;

    // reset all funds based on whether or not user allocates total amount to a fund
    const toFunds = Object.keys(allocateToFunds).reduce((acc, id) => {
      const selectedForTotal = agreementID === id;
      return {
        ...acc,
        [id]: props.totalAllocated
          ? {
              ...allocateToFunds[id],
              grossAmountDollars: 0,
              grossAmount: 0,
              grossUnit: UNIT.DOLLARS,
              disabled: !selectedForTotal,
              selected: selectedForTotal,
              ...(selectedForTotal && props), // add all sent props if selected for total
            }
          : {
              name: allocateToFunds[id].name,
              ticker: allocateToFunds[id].ticker,
              selected: true,
              disabled: false,
              totalAllocated: false,
            },
      };
    }, {});

    return {
      ...state,
      allocateToFunds: {
        ...toFunds,
      },
    };
  })

  .handleAction(act.ROTH_CONVERSION_SKIP_STEP_2, (state, { payload }) => {
    const { existingRothIRAFunds } = payload;
    const { convertFromFunds } = state;
    const doesIRAFundExist = (ticker) =>
      existingRothIRAFunds.find((f) => f.ticker === ticker) || {};

    const toBeConvertedFundMap = Object.keys(convertFromFunds).reduce(
      (acc, id) => {
        const currentFund = convertFromFunds[id];
        const iraVersion = doesIRAFundExist(currentFund.ticker);
        const agreementID = iraVersion?.agreementID || uuidv4();
        if (!isEmpty(iraVersion)) {
          return {
            ...acc,
            [agreementID]: {
              ...iraVersion,
              grossUnit: currentFund.grossUnit,
              grossAmount:
                currentFund.grossUnit === UNIT.PERCENT
                  ? 100
                  : currentFund.grossAmount,
              grossAmountDollars: currentFund.grossAmountDollars,
              newFund: false,
              agreementType: 'ROTH_IRA',
              ogAgreeNum: currentFund.agreementID,
            },
          };
        } else {
          return {
            ...acc,
            [agreementID]: {
              ...currentFund,
              grossAmount:
                currentFund.grossUnit === UNIT.PERCENT
                  ? 100
                  : currentFund.grossAmount,
              agreementID: `newfund-${Date.now()}`,
              newFund: isEmpty(iraVersion),
              agreementType: 'ROTH_IRA',
              ogAgreeNum: currentFund.agreementID,
            },
          };
        }
      },
      {}
    );
    return {
      ...state,
      allocateToFunds: toBeConvertedFundMap,
    };
  })

  // ADD NEW FUND (only in Step 2)
  .handleAction(act.ADD_NEW_ROTH_FUND_FULFILLED, (state, { payload }) => {
    const { fund }: { fund: Fund } = payload;
    const fundInfo = {
      ...fund,
      name: fund.fundName,
      ticker: fund.ticker,
      grossAmountDollars: 0,
      grossAmount: 0,
      grossUnit: UNIT.DOLLARS,
      userAdded: true,
      newFund: true,
      agreementType: 'ROTH_IRA',
      agreementID: fund.agreementID,
      trpCode: fund.trpCode,
    };

    return {
      ...state,
      allocateToFunds: {
        ...state.allocateToFunds,
        [fund.agreementID]: {
          ...fundInfo,
          selected: true,
        },
      },
      userAddedFunds: state.userAddedFunds?.concat([fundInfo]),
    };
  })

  /**
   * STEP 4
   */
  // updates Roth beneficiary type selection
  .handleAction(act.UPDATE_ROTH_BENEFICIARY_OPTION, (state, { payload }) => {
    const { beneficiaryOption, beneficiaries, i = 0 } = payload;
    return {
      ...state,
      beneficiaryOption,
      beneficiaries,
      beneficiaryIndex: i,
    };
  })

  // adds new roth beneficiary
  .handleAction(act.ADD_ROTH_BENEFICIARY, (state, { payload }) => {
    const { id, category } = payload;
    return {
      ...state,
      userAddedBeneficiaries: {
        ...state.userAddedBeneficiaries,
        [category]: {
          ...state.userAddedBeneficiaries?.[category],
          [id]: { entityType: BENEFICIARY_TYPES.INDIVIDUAL },
        },
      },
    };
  })

  // deletes a user added beneficiary
  .handleAction(act.DELETE_ROTH_BENEFICIARY, (state, { payload }) => {
    const { id, category } = payload;

    return {
      ...state,
      userAddedBeneficiaries: {
        ...state.userAddedBeneficiaries,
        [category]: omit(state.userAddedBeneficiaries[category], [id]),
      },
    };
  })

  // updates beneficiary
  .handleAction(act.UPDATE_ROTH_BENEFICIARY, (state, { payload }) => {
    const { id, category, props } = payload;
    const beneficiary = state.userAddedBeneficiaries?.[category]?.[id];
    return {
      ...state,
      userAddedBeneficiaries: {
        ...state.userAddedBeneficiaries,
        [category]: {
          ...state.userAddedBeneficiaries?.[category],
          [id]: {
            ...(!props.type && beneficiary),
            ...props,
          },
        },
      },
    };
  });
